import { isPlatformBrowser, NgIf } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  Output,
  PLATFORM_ID,
} from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { Filesystem } from '@capacitor/filesystem';
import { AlertFacade } from '@curbnturf/alert/src/lib/+state/alert.facade';
import { MiscSvgComponent } from '@curbnturf/custom-svgs/src/lib/misc-svg/misc-svg.component';
import { Logger } from '@curbnturf/network';
import { PhotoService } from '@curbnturf/photo';
import { IonicModule, ModalController } from '@ionic/angular';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { CroppieOptions } from 'croppie';
import { fromEvent, lastValueFrom, map, Observable, Subscription } from 'rxjs';
import { catchError, take, timeout } from 'rxjs/operators';
import { ImageUploadMobileComponent } from '../image-upload-mobile/image-upload-mobile.component';
import { ImageUploadOptions } from '../image-upload-options';
import { ImageUploaderBaseComponent } from '../image-uploader-base/image-uploader-base.component';

const IMAGE_COMPLETE_TIMEOUT = 30000;

@Component({
  selector: 'curbnturf-image-upload',
  templateUrl: './image-upload.component.html',
  styleUrls: ['./image-upload.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [ImageUploaderBaseComponent, ImageUploadMobileComponent, IonicModule, MiscSvgComponent, NgIf],
})
export class ImageUploadComponent implements OnDestroy {
  @Input() options: ImageUploadOptions;
  @Input() text = 'Take or Upload an Image';

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() delayedSaveFunction?: (files: any, dispatchSave: (files: any) => void) => void;

  @Input() updatedFunction: () => void;
  @Output() save = new EventEmitter<FormData>();
  @Output() updated = new EventEmitter<string>();
  @Output() beforeUpdating = new EventEmitter<void>();

  widthPx = '200';
  heightPx = '200';
  currentImage?: string;
  croppieImage?: string;
  croppiedImage?: string;
  nativePath?: string;
  originalBlob: Blob;
  maxDimensions: { width: number; height: number };
  caption: string;
  spinnerOn: boolean;

  modalId: number = Math.round(Math.random() * 10000);
  imageModal?: HTMLIonModalElement;

  subscriptions: Subscription[] = [];

  get heightRatio(): string {
    if (!this.options || this.spinnerOn) {
      return '0';
    }

    if (this.options.croppieOptions && this.options.croppieOptions.boundary) {
      return `${this.options.croppieOptions.boundary.height}px`;
    }
    if (this.options.output && this.options.output.size) {
      const size: 'viewport' | 'original' | { height: number; width: number } = this.options.output.size;

      if (typeof size !== 'string' && size.height && size.width) {
        return `${(size.height / size.width) * 100}%`;
      }
    }

    return '0';
  }

  get widthRatio(): string {
    if (!this.options) {
      return '0';
    }

    if (this.options.croppieOptions && this.options.croppieOptions.boundary) {
      return `${this.options.croppieOptions.boundary.width}px`;
    }
    if (this.options.output && this.options.output.size) {
      const size: 'viewport' | 'original' | { height: number; width: number } = this.options.output.size;

      if (typeof size !== 'string' && size.height && size.width) {
        return `${(size.width / size.height) * 100}%`;
      }
    }

    return '0';
  }

  get imageToDisplay() {
    if (this.currentImage) {
      return this.currentImage;
    }
    // Why this? Maybe we should make our own somehow.
    // @todo this isn't going to work when offline, I should rewrite it somehow
    return `https://placehold.it/${
      this.options?.output?.size && typeof this.options.output.size !== 'string' && this.options.output.size.width
        ? this.options.output.size.width
        : this.widthPx
    }x${
      this.options?.output?.size && typeof this.options.output.size !== 'string' && this.options.output.size.height
        ? this.options.output.size.height
        : this.heightPx
    }`;
  }

  get croppieOptions(): CroppieOptions {
    const opts: CroppieOptions = {};
    opts.enableOrientation = true;
    opts.showZoomer = true;
    return { ...opts, ...this.options?.croppieOptions };
  }

  constructor(
    private actions$: Actions,
    private alertFacade: AlertFacade,
    private cdr: ChangeDetectorRef,
    private logger: Logger,
    private modalController: ModalController,
    private photoService: PhotoService,
    private store: Store,
    @Inject(PLATFORM_ID) private platformId: string,
  ) {}

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => {
      if (!sub.closed) {
        sub.unsubscribe();
      }
    });
  }

  async saveImageFromCroppie(files: FormData) {
    this.spinnerOn = true;

    if (this.options.action && typeof this.options.action === 'function') {
      this.beforeUpdating.emit();

      // timeout allows anything before the update to have a chance of happening
      setTimeout(() => {
        this.waitForSave(files);
      }, 1500);
    } else {
      this.save.emit(files);
    }

    if (Array.isArray(this.options.files)) {
      this.options.files.push(this.currentImage);
    }

    if (Array.isArray(this.options.captions)) {
      this.options.captions.push(this.caption);
    }

    await this.cancelCroppieEdit();
    this.exit();
  }

  async cancelCroppieEdit() {
    delete this.croppieImage;
    delete this.croppiedImage;
    await this.closeModal();
    this.cdr.detectChanges();
  }

  async closeModal() {
    if (this.imageModal) {
      await this.imageModal.dismiss();
      delete this.imageModal;
    }
  }

  isString(possibleString: unknown): boolean {
    return Boolean(possibleString) && typeof possibleString === 'string';
  }

  async selectImage() {
    const photo = await this.photoService.getCameraPhoto();
    if (Capacitor.isNativePlatform()) {
      this.nativePath = photo.path;
    }
    this.croppieImage = photo.webPath;

    try {
      if (photo.webPath) {
        const getImgSize = (imageSrc: string): Observable<{ width: number; height: number }> => {
          let mapLoadedImage = (event: any): { width: number; height: number } => {
            return {
              width: event.target.width,
              height: event.target.height,
            };
          };
          var image = new Image();
          let $loadedImg = fromEvent(image, 'load').pipe(take(1), map(mapLoadedImage));
          image.src = imageSrc;

          return $loadedImg;
        };
        this.maxDimensions = await lastValueFrom(getImgSize(photo.webPath));
      }

      if (this.croppieImage) {
        let data: Blob;
        if (Capacitor.isNativePlatform()) {
          if (this.nativePath) {
            // Read the file into base64 format
            const file = await Filesystem.readFile({
              path: this.nativePath,
            });

            const byteCharacters = atob(file.data as string);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            let options: object | undefined = undefined;
            if (ImageUploadComponent.isAcceptableFormat(photo.format)) {
              options = { type: 'image/' + photo.format };
            }
            data = new Blob([byteArray], options);
          } else {
            // Blank blob if for some reason the file can't be found.
            throw new Error('File Path Not Found');
          }
        } else {
          // Fetch the photo, read as a blob, then convert to base64 format
          const response = await fetch(this.croppieImage);
          data = await response.blob();
        }
        this.originalBlob = data;
      } else {
        throw new Error('Original File Not Found');
      }
    } catch (e) {
      this.alertFacade.display({
        body: 'An error has occurred while trying to upload your image. Please try again.',
        title: 'Error Uploading Image',
        level: 'error',
      });
      this.logger.error('Error uploading profile photo', e, e.stack, 'user');
    }

    await this.openModal();
    this.cdr.detectChanges();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  protected dispatchSave(files: FormData) {
    if (this.options.action) {
      this.store.dispatch(
        this.options.action({
          formData: files,
          data: this.options.data,
        }),
      );
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  protected waitForSave(files: any) {
    if (this.delayedSaveFunction) {
      this.delayedSaveFunction(files, this.dispatchSave.bind(this));
    } else {
      this.dispatchSave(files);
    }
  }

  protected async openModal() {
    this.imageModal = await this.modalController.create({
      component: ImageUploadMobileComponent,
      componentProps: {
        croppieImage: this.croppieImage,
        originalBlob: this.originalBlob,
        maxDimensions: this.maxDimensions,
        saveFunction: (files: FormData) => this.saveImageFromCroppie(files),
        options: this.options,
      },
    });

    // after closing the image upload modal scroll back to the same position
    if (isPlatformBrowser(this.platformId) && window) {
      const scrollX = window.scrollX;
      const scrollY = window.scrollY;
      await this.imageModal.present();

      this.imageModal.onDidDismiss().then(() => window.scrollTo(scrollX, scrollY));
    }
  }

  protected exit() {
    if (this.options.completeAction) {
      this.subscriptions.push(
        this.actions$
          .pipe(
            ofType(this.options.completeAction.type),
            take(1),
            timeout(IMAGE_COMPLETE_TIMEOUT),
            catchError(async (err) => {
              this.logger.error('Error while uploading image.', { error: err });
              this.spinnerOn = false;
              this.caption = '';
              this.currentImage = '';
              this.croppieImage = '';
              this.croppiedImage = '';
              this.cdr.detectChanges();
              return err;
            }),
          )
          .subscribe(async () => {
            this.updated.emit(this.currentImage);
            this.updatedFunction && this.updatedFunction();
            this.spinnerOn = false;
            this.caption = '';
            this.currentImage = '';
            this.croppieImage = '';
            this.croppiedImage = '';
            this.cdr.detectChanges();
          }),
      );
    } else {
      setTimeout(() => {
        this.updated.emit(this.currentImage);
        this.updatedFunction && this.updatedFunction();
        this.spinnerOn = false;
        this.caption = '';
        this.currentImage = '';
        this.croppieImage = '';
        this.croppiedImage = '';
        this.cdr.detectChanges();
      }, 1000);
    }
  }

  private static isAcceptableFormat(format: string) {
    switch (format) {
      case 'jpeg':
      case 'jpg':
      case 'png':
        return true;
      default:
        return false;
    }
  }
}
